import { featureService } from '@common/api/featureService';
import {
    useCallback, useEffect, useRef, useState
} from 'react';
import { AUDIT_LOG_SYSTEM_PERMISSION, Permission } from './permissions';
import { WorkspaceRole } from './useWorkspaces';
import {
    type PermissionLoadStatus,
    type PermissionResolution,
    resolvePermissionLoadStatus,
} from './permissionResolutionState';

/**
 * Role Permission 数据结构
 */
export interface RolePermission {
    role: WorkspaceRole;
    permission: string;
    allowed: boolean;
    created_at: string;
    updated_at: string;
}

/**
 * Hook 返回类型
 */
export interface UsePermissionsReturn {
    status: PermissionLoadStatus;
    isLoading: boolean;
    error: Error | null;
    role: string | null;
    roleKey: string | null;
    roleName: string | null;
    isOwner: boolean;
    isAdmin: boolean;
    isAdminRole: boolean;
    canTransferOwnership: boolean;
    checkPermissions: (permissions: Permission[]) => boolean;
    refresh: () => Promise<void>;
}

const normalizeWorkspaceRole = (value?: string | null): WorkspaceRole | null => {
    const normalized = String(value || '').trim().toUpperCase();
    if (normalized === 'OWNER' || normalized === 'ADMIN' || normalized === 'MEMBER') {
        return normalized;
    }
    return null;
};

const trimToNull = (value?: string | null): string | null => {
    const trimmed = String(value || '').trim();
    return trimmed || null;
};

const resolveCapabilityRoleKey = (capabilities: {
    role?: string | null;
    workspaceRoleKey?: string | null;
}): WorkspaceRole | null => {
    return normalizeWorkspaceRole(capabilities.workspaceRoleKey || capabilities.role);
};

const resolveCapabilityRoleDisplay = (capabilities: {
    role?: string | null;
    workspaceRoleKey?: string | null;
    workspaceRoleName?: string | null;
}): string | null => {
    return trimToNull(capabilities.workspaceRoleName)
        || trimToNull(capabilities.workspaceRoleKey)
        || trimToNull(capabilities.role);
};

const expandWorkspaceRolePermissions = (rawPermissions: string[] = []) => {
    const permissionSet = new Set(rawPermissions);

    if (permissionSet.has('workspace_settings')) {
        permissionSet.add(Permission.WORKSPACE_UPDATE);
    }

    if (permissionSet.has('members')) {
        permissionSet.add(Permission.MEMBER_INVITE);
        permissionSet.add(Permission.MEMBER_REMOVE);
    }

    if (permissionSet.has('roles_access')) {
        permissionSet.add(Permission.MEMBER_CHANGE_ROLE);
    }

    if (permissionSet.has('integrations')) {
        permissionSet.add(Permission.INTEGRATION_VIEW);
        permissionSet.add(Permission.INTEGRATION_CONNECT);
        permissionSet.add(Permission.INTEGRATION_DISCONNECT);
    }

    if (permissionSet.has('billing')) {
        permissionSet.add(Permission.SUBSCRIPTION_VIEW);
        permissionSet.add(Permission.SUBSCRIPTION_MANAGE);
    }

    if (permissionSet.has('usage')) {
        permissionSet.add(Permission.USAGE_VIEW);
    }

    if (permissionSet.has('outcome_ledger') || permissionSet.has('outcome_summary')) {
        permissionSet.add(Permission.OUTCOME_SUMMARY_VIEW);
    }

    if (permissionSet.has('outcome_ledger:manage') || permissionSet.has('outcome_summary_manage')) {
        permissionSet.add(Permission.OUTCOME_SUMMARY_MANAGE);
        permissionSet.add(Permission.OUTCOME_SUMMARY_VIEW);
    }

    if (permissionSet.has(AUDIT_LOG_SYSTEM_PERMISSION) || permissionSet.has('agent_run_audit')) {
        permissionSet.add(Permission.AUDIT_LOG_VIEW);
    }

    return [...permissionSet];
};

/**
 * 获取角色的权限
 * 根据当前 workspace capability 接口返回的系统权限判断功能入口。
 */
export const usePermissions = (role: WorkspaceRole | null, workspaceId?: string | null): UsePermissionsReturn => {
    const normalizedInputRole = normalizeWorkspaceRole(role);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [capabilityRoleDisplay, setCapabilityRoleDisplay] = useState<string | null>(null);
    const [capabilityRoleKey, setCapabilityRoleKey] = useState<WorkspaceRole | null>(null);
    const [capabilityRawRoleKey, setCapabilityRawRoleKey] = useState<string | null>(null);
    const [capabilityRoleName, setCapabilityRoleName] = useState<string | null>(null);
    const [capabilityFlags, setCapabilityFlags] = useState({
        isOwner: false,
        isAdminRole: false,
        canTransferOwnership: false,
    });
    const fetchRequestIdRef = useRef(0);
    const lastWorkspaceIdRef = useRef<string | null>(null);
    const [resolution, setResolution] = useState<PermissionResolution>({
        workspaceId: null,
        status: 'idle',
    });
    const [error, setError] = useState<Error | null>(null);
    /**
     * 从后端 capability 接口拉取当前用户的实际权限。
     * 注意：刷新时保留旧权限数据，直到新数据到达，避免 UI 闪烁。
     */
    const fetchPermissions = useCallback(async () => {
        const requestId = fetchRequestIdRef.current + 1;
        fetchRequestIdRef.current = requestId;
        const workspaceChanged = lastWorkspaceIdRef.current !== workspaceId;
        lastWorkspaceIdRef.current = workspaceId || null;

        setError(null);

        if (workspaceChanged) {
            setPermissions([]);
            setCapabilityRoleDisplay(null);
            setCapabilityRoleKey(null);
            setCapabilityRawRoleKey(null);
            setCapabilityRoleName(null);
            setCapabilityFlags({
                isOwner: false,
                isAdminRole: false,
                canTransferOwnership: false,
            });
        }

        if (!role || !workspaceId) {
            // 只有在明确无 role/workspace 时才清空权限（登出或切换 workspace）
            setPermissions([]);
            setCapabilityRoleDisplay(null);
            setCapabilityRoleKey(null);
            setCapabilityRawRoleKey(null);
            setCapabilityRoleName(null);
            setCapabilityFlags({
                isOwner: false,
                isAdminRole: false,
                canTransferOwnership: false,
            });
            setResolution({
                workspaceId: null,
                status: 'idle',
            });
            return;
        }

        setResolution({
            workspaceId,
            status: 'loading',
        });

        try {
            const capabilities = await featureService.getWorkspaceCapabilities(workspaceId);
            if (fetchRequestIdRef.current !== requestId) return;
            setCapabilityRoleDisplay(resolveCapabilityRoleDisplay(capabilities));
            setCapabilityRoleKey(resolveCapabilityRoleKey(capabilities));
            setCapabilityRawRoleKey(trimToNull(capabilities.workspaceRoleKey || capabilities.role));
            setCapabilityRoleName(trimToNull(capabilities.workspaceRoleName));
            setCapabilityFlags({
                isOwner: Boolean(capabilities?.isOwner),
                isAdminRole: Boolean(capabilities?.isAdminRole ?? capabilities?.isAdmin),
                canTransferOwnership: Boolean(capabilities?.canTransferOwnership),
            });
            setPermissions(expandWorkspaceRolePermissions(capabilities.permissions));
            setResolution({
                workspaceId,
                status: 'ready',
            });
        } catch (err: any) {
            if (fetchRequestIdRef.current !== requestId) return;
            console.warn('[usePermissions] Failed to fetch workspace capabilities', err);
            setError(err);
            setPermissions([]);
            setCapabilityRoleDisplay(null);
            setCapabilityRoleKey(null);
            setCapabilityRawRoleKey(null);
            setCapabilityRoleName(null);
            setCapabilityFlags({
                isOwner: false,
                isAdminRole: false,
                canTransferOwnership: false,
            });
            setResolution({
                workspaceId,
                status: 'error',
            });
        }
    }, [role, workspaceId]);

    // 初始化和 role 变化时拉取权限
    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    /**
     * 检查是否有所有指定的权限
     * @param permissionList - 要检查的权限列表
     * @returns 是否拥有所有权限
     */
    const checkPermissions = useCallback((permissionList: Permission[]): boolean => {
        return permissionList.every(permission => permissions.includes(permission));
    }, [permissions]);

    const effectiveRoleKey = capabilityRoleKey || normalizedInputRole;
    const status = resolvePermissionLoadStatus({
        role: normalizedInputRole,
        workspaceId,
        resolution,
    });

    return {
        status,
        isLoading: status === 'loading',
        error,
        role: capabilityRoleDisplay || effectiveRoleKey,
        roleKey: capabilityRawRoleKey || effectiveRoleKey,
        roleName: capabilityRoleName,
        isOwner: capabilityFlags.isOwner || effectiveRoleKey === 'OWNER',
        isAdmin: capabilityFlags.isAdminRole || effectiveRoleKey === 'ADMIN',
        isAdminRole: capabilityFlags.isAdminRole || effectiveRoleKey === 'ADMIN',
        canTransferOwnership: capabilityFlags.canTransferOwnership,
        checkPermissions,
        refresh: fetchPermissions,
    };
};
